import { z } from 'zod';
import type { BaseRecord } from './sync';

/**
 * Ressources, annonces et notifications (CDC §15).
 *  - Annonces : publiées par un administrateur ou un superviseur autorisé (`announcement.publish`), pour tous, pour
 *    certains rôles (commerciaux, techniciens…) et / ou certains territoires. Urgente = notification push + badge
 *    « non lu » ; épinglable ; jamais modifiée après publication (archivage seulement).
 *  - Ressources : flyers et documents téléchargeables, classés par catégorie (liste administrable), datés et
 *    versionnés (chaque nouvelle version garde les précédentes), avec le même ciblage.
 *  - Notifications : chaque type d'événement est réglable par rôle — désactivé, dans l'application, ou push.
 */

/* ------------------------------------------------------------------ ciblage */

/** Empty lists mean « everyone ». */
export interface Audience {
  audienceRoles: string[];
  territoryIds: string[];
}

/** Whether a user (role codes + territories) belongs to an audience. */
export function inAudience(a: Audience, user: { roleCodes: string[]; territoryIds: string[] }): boolean {
  const roleOk = !a.audienceRoles.length || a.audienceRoles.some((r) => user.roleCodes.includes(r));
  const terrOk = !a.territoryIds.length || a.territoryIds.some((t) => user.territoryIds.includes(t));
  return roleOk && terrOk;
}

const audienceShape = {
  audienceRoles: z.array(z.string().trim().min(1).max(60)).max(20).default([]),
  territoryIds: z.array(z.string().uuid()).max(50).default([]),
};

/* ------------------------------------------------------------------ annonces */

export interface AnnouncementRecord extends BaseRecord, Audience {
  title: string;
  body: string;
  urgent: boolean;
  pinned: boolean;
  publishedAt: string;
  publishedById: string;
  publishedByName: string;
  /** hidden from the recipients after this day (inclusive) */
  expiresOn: string | null;
  archivedAt: string | null;
  /** for the viewer */
  readAt: string | null;
}

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date invalide');

export const announcementInput = z.object({
  id: z.string().uuid(),
  title: z.string().trim().min(1, 'Titre obligatoire').max(200),
  body: z.string().trim().min(1, 'Message obligatoire').max(5000),
  urgent: z.boolean().default(false),
  pinned: z.boolean().default(false),
  expiresOn: day.nullish().transform((v) => v ?? null),
  ...audienceShape,
});
export type AnnouncementInput = z.input<typeof announcementInput>;

/** After publication only the pin and the archive change (the message itself stays as sent). */
export const announcementPatch = z.object({ pinned: z.boolean().optional(), archived: z.boolean().optional() });

/** Recipients' view: live (not archived, not expired), pinned first then most recent. */
export function liveAnnouncements<T extends Pick<AnnouncementRecord, 'archivedAt' | 'expiresOn' | 'pinned' | 'publishedAt'>>(list: T[], today: string): T[] {
  return list
    .filter((a) => !a.archivedAt && (!a.expiresOn || a.expiresOn >= today))
    .sort((a, b) => Number(b.pinned) - Number(a.pinned) || b.publishedAt.localeCompare(a.publishedAt));
}

/* ------------------------------------------------------------------ ressources */

export const RESOURCE_CATEGORY = 'resource_category';

export interface ResourceVersion {
  id: string;
  versionNo: number;
  fileName: string;
  mime: string;
  sizeBytes: number;
  note: string | null;
  /** date of the document (e.g. « tarifs au 01/10/2026 ») */
  validFrom: string;
  publishedAt: string;
  publishedByName: string;
}

export interface ResourceRecord extends BaseRecord, Audience {
  title: string;
  description: string | null;
  categoryId: string;
  isActive: boolean;
  createdAt: string;
  /** most recent first; [0] is the current version */
  versions: ResourceVersion[];
}

export const resourceInput = z.object({
  id: z.string().uuid(),
  title: z.string().trim().min(1, 'Titre obligatoire').max(200),
  description: z.string().trim().max(2000).nullish().transform((v) => v || null),
  categoryId: z.string().uuid(),
  ...audienceShape,
});
export const resourcePatch = z
  .object({
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().max(2000).nullable(),
    categoryId: z.string().uuid(),
    audienceRoles: z.array(z.string().trim().min(1).max(60)).max(20),
    territoryIds: z.array(z.string().uuid()).max(50),
    isActive: z.boolean(),
  })
  .partial();
export const resourceVersionMeta = z.object({
  name: z.string().trim().min(1).max(200),
  note: z.string().trim().max(500).nullish().transform((v) => v || null),
  validFrom: day.optional(),
});

/* ------------------------------------------------------------------ notifications configurables */

export const NOTIFICATION_LEVELS = ['off', 'app', 'push'] as const;
export type NotificationLevel = (typeof NOTIFICATION_LEVELS)[number];
export const NOTIFICATION_LEVEL_LABELS: Record<NotificationLevel, string> = { off: 'Désactivée', app: 'Dans l’application', push: 'Push' };
const RANK: Record<NotificationLevel, number> = { off: 0, app: 1, push: 2 };

export interface NotificationEventDef {
  code: string;
  label: string;
  group: string;
  defaultLevel: NotificationLevel;
  /** cannot be lowered (an urgent announcement is always pushed) */
  locked?: boolean;
}

const ev = (code: string, group: string, label: string, defaultLevel: NotificationLevel, locked = false): NotificationEventDef => ({ code, group, label, defaultLevel, locked });

/** §3 : push by default for the important events (dossier returned / validated, intervention proposed / refused,
 * planning changed, urgent announcement, expiring document, SAV). */
export const NOTIFICATION_EVENTS: NotificationEventDef[] = [
  ev('dossier.validated', 'Dossiers', 'Dossier validé', 'push'),
  ev('dossier.returned', 'Dossiers', 'Dossier renvoyé « À corriger »', 'push'),
  ev('prospect.assigned', 'Prospects', 'Nouveaux prospects attribués', 'push'),
  ev('prospect.won', 'Prospects', 'Prospect validé par le client', 'app'),
  ev('portal.client_updated', 'Portail client', 'Coordonnées complétées par le client', 'app'),
  ev('portal.file_uploaded', 'Portail client', 'Document déposé par le client', 'app'),
  ev('portal.signed', 'Portail client', 'Dossier signé à distance', 'push'),
  ev('intervention.proposed', 'Interventions', 'Intervention proposée', 'push'),
  ev('intervention.refused', 'Interventions', 'Intervention refusée', 'push'),
  ev('intervention.accepted', 'Interventions', 'Intervention acceptée (planifiée automatiquement)', 'app'),
  ev('intervention.scheduled', 'Interventions', 'Intervention confirmée', 'app'),
  ev('planning.changed', 'Interventions', 'Planning modifié', 'push'),
  ev('intervention.withdrawn', 'Interventions', 'Intervention retirée', 'push'),
  ev('intervention.cancelled', 'Interventions', 'Intervention annulée', 'push'),
  ev('intervention.impossible', 'Interventions', 'Intervention impossible', 'push'),
  ev('intervention.completed', 'Interventions', 'Intervention terminée', 'app'),
  ev('discount.management', 'Dossiers', 'Remise « accord dirigeant / responsable » accordée', 'push', true),
  ev('payment.confirmed', 'Paiements', 'Encaissement confirmé', 'app'),
  ev('payment.succeeded', 'Paiements', 'Paiement carte reçu', 'app'),
  ev('payment.failed', 'Paiements', 'Paiement carte échoué', 'push'),
  ev('payment.refunded', 'Paiements', 'Paiement remboursé', 'app'),
  ev('payment.partially_refunded', 'Paiements', 'Remboursement partiel', 'app'),
  ev('payment.disputed', 'Paiements', 'Litige sur un paiement', 'push'),
  ev('commission.validated', 'Commissions', 'Commission / rémunération validée', 'app'),
  ev('commission.paid', 'Commissions', 'Commission / rémunération payée', 'app'),
  ev('sav.to_validate', 'SAV', 'SAV à valider', 'push'),
  ev('sav.to_plan', 'SAV', 'SAV à planifier', 'push'),
  ev('sav.reject', 'SAV', 'SAV refusé', 'push'),
  ev('sav.close', 'SAV', 'SAV clôturé', 'app'),
  ev('sav.intervention_completed', 'SAV', 'Intervention SAV terminée', 'app'),
  ev('contractor_document.to_review', 'Documents', 'Document sous-traitant à contrôler', 'app'),
  ev('contractor_document.approved', 'Documents', 'Document contrôlé', 'app'),
  ev('contractor_document.rejected', 'Documents', 'Document refusé', 'push'),
  ev('contractor_document.expiring', 'Documents', 'Document bientôt expiré', 'push'),
  ev('contractor_document.expired', 'Documents', 'Document expiré', 'push'),
  ev('invoice.to_regularize', 'Facturation', 'Dossier à régulariser en facturation', 'push'),
  ev('stock.alert', 'Stock', 'Stock faible / indisponible', 'app'),
  ev('announcement', 'Annonces', 'Nouvelle annonce', 'off'),
  ev('announcement.urgent', 'Annonces', 'Annonce urgente', 'push', true),
];

/** event → role code → level (absent = default of the event). */
export type NotificationSettings = Record<string, Record<string, NotificationLevel>>;

export const notificationSettingsInput = z.record(z.string().max(80), z.record(z.string().max(60), z.enum(NOTIFICATION_LEVELS)));

export function notificationEvent(kind: string): NotificationEventDef | undefined {
  return NOTIFICATION_EVENTS.find((e) => e.code === kind);
}

/**
 * Level for a user holding several roles: the highest of his roles (a technician who is also a commercial is not
 * silenced by one of them). Unknown kinds stay in the application.
 */
export function notificationLevel(settings: NotificationSettings | undefined, kind: string, roleCodes: string[]): NotificationLevel {
  const def = notificationEvent(kind);
  if (!def) return 'app';
  if (def.locked) return def.defaultLevel;
  const row = settings?.[def.code] ?? {};
  if (!roleCodes.length) return def.defaultLevel;
  return roleCodes.map((r) => row[r] ?? def.defaultLevel).reduce((a, b) => (RANK[b] > RANK[a] ? b : a), 'off' as NotificationLevel);
}

/* ------------------------------------------------------------------ appareils (push) */

export const PUSH_PLATFORMS = ['ios', 'android', 'web'] as const;
export const pushDeviceInput = z.object({
  token: z.string().trim().min(10).max(500),
  platform: z.enum(PUSH_PLATFORMS),
});
